import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlay, FiPause, FiVolume2, FiVolumeX, FiMaximize, 
  FiChevronRight, FiCheckCircle, FiRotateCcw, FiRotateCw 
} from 'react-icons/fi';
import testService from '../../../services/testService';

const VideoModal = ({ isOpen, videos, onComplete, testName }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hasFinishedCurrent, setHasFinishedCurrent] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [maxTimeWatched, setMaxTimeWatched] = useState(0);
  
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const baseURL = testService.getImageBaseURL();
  const currentVideoUrl = videos && videos[currentVideoIndex] 
    ? (videos[currentVideoIndex].startsWith('http') ? videos[currentVideoIndex] : `${baseURL}${videos[currentVideoIndex]}`)
    : '';

  useEffect(() => {
    if (isOpen) {
      setCurrentVideoIndex(0);
      setHasFinishedCurrent(false);
      setProgress(0);
      setMaxTimeWatched(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      setIsPlaying(false);
      setHasFinishedCurrent(false);
      setProgress(0);
      setCurrentTime(0);
      setMaxTimeWatched(0);
    }
  }, [currentVideoIndex]);

  const togglePlay = (e) => {
    if (e) e.stopPropagation();
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && !isScrubbing) {
      const cur = videoRef.current.currentTime;
      const dur = videoRef.current.duration;
      setCurrentTime(cur);
      
      // Track maximum time watched
      if (cur > maxTimeWatched) {
        setMaxTimeWatched(cur);
      }

      const currentProgress = (cur / dur) * 100;
      setProgress(currentProgress);
      
      if (currentProgress > 98) {
        setHasFinishedCurrent(true);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const val = parseFloat(e.target.value);
    const seekTime = (val / 100) * duration;
    
    // Prevent seeking forward beyond what has been watched
    if (seekTime > maxTimeWatched) {
      return;
    }

    setProgress(val);
    if (!isScrubbing) {
      if (videoRef.current) videoRef.current.currentTime = seekTime;
    }
  };

  const handleSeekStart = () => {
    setIsScrubbing(true);
  };

  const handleSeekEnd = (e) => {
    setIsScrubbing(false);
    if (videoRef.current) {
      const val = parseFloat(e.target.value);
      let seekTime = (val / 100) * duration;
      
      // Prevent seeking forward beyond what has been watched
      if (seekTime > maxTimeWatched) {
        seekTime = maxTimeWatched;
        setProgress((maxTimeWatched / duration) * 100);
      }
      
      videoRef.current.currentTime = seekTime;
    }
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      const newTime = videoRef.current.currentTime + seconds;
      
      // Prevent skipping forward beyond what has been watched
      if (seconds > 0 && newTime > maxTimeWatched) {
        return;
      }
      
      videoRef.current.currentTime = Math.max(0, Math.min(newTime, duration));
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setHasFinishedCurrent(true);
  };

  const handleNext = () => {
    if (currentVideoIndex < videos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const nextMute = !isMuted;
      videoRef.current.muted = nextMute;
      setIsMuted(nextMute);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (!document.fullscreenElement) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="assess-mod-student-video-modal fixed inset-0 z-[9999] bg-slate-900/95 backdrop-blur-md flex flex-col items-center justify-center p-4 md:p-8 overflow-y-auto"
      >
        {/* Video Player Container */}
        <motion.div 
          ref={containerRef}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="relative w-full max-w-5xl aspect-video bg-black rounded-3xl overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.5)] border border-white/10 group"
          onMouseMove={handleMouseMove}
        >
          <video
            ref={videoRef}
            className="w-full h-full object-contain cursor-pointer"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
            onClick={togglePlay}
            playsInline
          >
            <source src={currentVideoUrl} type="video/mp4" />
          </video>

          {/* Big Play Button (Overlay) */}
          {!isPlaying && (
            <div 
              className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer transition-colors hover:bg-black/20"
              onClick={togglePlay}
            >
              <motion.div 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-24 h-24 bg-red-600 rounded-full flex items-center justify-center text-white shadow-2xl shadow-red-600/40"
              >
                <FiPlay size={40} className="ml-1" />
              </motion.div>
            </div>
          )}

          {/* Controls */}
          <AnimatePresence>
            {showControls && (
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/95 via-black/60 to-transparent"
              >
                {/* Custom Seek Bar - Increased spacing */}
                <div className="relative group/seek mb-8">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={handleSeek}
                    onMouseDown={handleSeekStart}
                    onMouseUp={handleSeekEnd}
                    onTouchStart={handleSeekStart}
                    onTouchEnd={handleSeekEnd}
                    className="absolute inset-0 w-full h-1.5 opacity-0 cursor-pointer z-10"
                  />
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-red-600 relative"
                      style={{ width: `${progress}%` }}
                    >
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-xl scale-0 group-hover/seek:scale-100 transition-transform ring-4 ring-red-600/20" />
                    </motion.div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6 md:gap-8">
                    <div className="flex items-center gap-4">
                      <button onClick={() => skip(-10)} className="text-white/70 hover:text-white transition-colors" title="Back 10s">
                        <FiRotateCcw size={22} />
                      </button>

                      <button onClick={togglePlay} className="text-white hover:scale-110 transition-all">
                        {isPlaying ? <FiPause size={32} /> : <FiPlay size={32} />}
                      </button>

                      <button 
                        onClick={() => skip(10)} 
                        className={`transition-colors ${currentTime + 10 > maxTimeWatched ? 'text-white/20 cursor-not-allowed' : 'text-white/70 hover:text-white'}`} 
                        disabled={currentTime + 10 > maxTimeWatched}
                        title="Forward 10s"
                      >
                        <FiRotateCw size={22} />
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-4 group/volume bg-white/5 px-4 py-2 rounded-xl border border-white/5">
                      <button onClick={toggleMute} className="text-white/70 hover:text-white transition-colors">
                        {isMuted || volume === 0 ? <FiVolumeX size={22} /> : <FiVolume2 size={22} />}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        className="w-0 group-hover/volume:w-24 transition-all duration-300 h-1 bg-white/20 rounded-full accent-red-600 cursor-pointer"
                      />
                    </div>

                    <div className="text-white/60 text-xs font-black tracking-widest bg-white/5 px-3 py-1.5 rounded-lg border border-white/5">
                      {formatTime(currentTime)} <span className="text-white/20 px-1">/</span> {formatTime(duration)}
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    <div className="hidden xl:block text-right">
                      <div className="text-xs font-black text-white uppercase tracking-wider">{testName}</div>
                    </div>
                    
                    <div className="h-10 w-px bg-white/10 hidden md:block" />

                    <button 
                      onClick={toggleFullscreen}
                      className="text-white/70 hover:text-white transition-all p-2 hover:bg-white/10 rounded-xl border border-transparent hover:border-white/10"
                    >
                      <FiMaximize size={24} />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Top Info Bar - Better alignment */}
          <div className="absolute top-0 left-0 right-0 p-8 flex justify-between items-start pointer-events-none">
            <div className="bg-black/60 backdrop-blur-xl px-6 py-2.5 rounded-full border border-white/10 shadow-2xl">
              <span className="text-[10px] font-black uppercase tracking-[3px] text-white">
                STEP {currentVideoIndex + 1} OF {videos.length}
              </span>
            </div>
            
            {hasFinishedCurrent && (
              <motion.div 
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                className="bg-green-500 text-white text-[10px] font-black uppercase px-6 py-2.5 rounded-full shadow-2xl shadow-green-500/20 flex items-center gap-2 border border-green-400/50"
              >
                <FiCheckCircle size={16} /> Completed
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Action Buttons - Centered and Balanced */}
        <div className="mt-12 flex flex-col items-center gap-6 w-full max-w-2xl">
          <div className="flex items-center justify-center gap-6 w-full">
            {currentVideoIndex > 0 && (
              <motion.button
                whileHover={{ scale: 1.02, x: -5 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleBack}
                className="h-16 px-10 rounded-2xl flex items-center gap-4 bg-white/5 text-white/50 hover:bg-white/10 hover:text-white transition-all duration-300 border border-white/5"
              >
                <FiChevronRight className="rotate-180 text-xl" />
                <span className="text-xs font-black uppercase tracking-[0.2em]">
                  Previous Step
                </span>
              </motion.button>
            )}

            <motion.button
              whileHover={hasFinishedCurrent ? { scale: 1.02, x: 5 } : {}}
              whileTap={hasFinishedCurrent ? { scale: 0.98 } : {}}
              disabled={!hasFinishedCurrent}
              onClick={handleNext}
              className={`
                h-16 relative overflow-hidden group px-12 rounded-2xl flex items-center gap-6 transition-all duration-300
                ${hasFinishedCurrent 
                  ? 'bg-red-600 text-white shadow-2xl shadow-red-600/40 hover:bg-red-700 cursor-pointer min-w-[280px]' 
                  : 'bg-white/5 text-white/20 cursor-not-allowed border border-white/5 min-w-[280px]'}
              `}
            >
              <span className="text-xs font-black uppercase tracking-[0.2em] flex-1 text-center">
                {currentVideoIndex < videos.length - 1 ? 'Next Video' : 'Start Assessment'}
              </span>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${hasFinishedCurrent ? 'bg-white/20 group-hover:bg-white/30' : 'bg-white/5'}`}>
                {currentVideoIndex < videos.length - 1 ? <FiChevronRight className="text-xl" /> : <FiCheckCircle className="text-xl" />}
              </div>
            </motion.button>
          </div>
          
          <AnimatePresence>
            {!hasFinishedCurrent && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/5 px-6 py-2 rounded-full border border-white/5 flex items-center gap-3"
              >
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.5)]" />
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">
                  Watch the full video to start the assessment
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Branding - Subtle Footer */}
        <div className="mt-12 opacity-30 hover:opacity-100 transition-all duration-500 flex items-center gap-4">
          <div className="h-px w-12 bg-white/20" />
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center text-white font-black text-sm border border-white/10 italic">e</div>
            <div className="text-[10px] font-black text-white uppercase tracking-[0.2em]">eNova Assessment Portal</div>
          </div>
          <div className="h-px w-12 bg-white/20" />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VideoModal;

